import type { Accept } from "react-dropzone";

/**
 * What the photo picker will accept.
 *
 * This used to enumerate 64 individual entries — every image MIME type anyone
 * could think of, including `image/hej2k`, `image/x-xwindowdump`, `image/jls`
 * and `image/jxr` — with no `image/*` wildcard among them. react-dropzone joins
 * that object into the `accept` attribute of the real <input type="file">, so
 * what iOS's photo picker received was a 613-character, 64-entry list of mostly
 * MIME types Apple has no UTI mapping for. `image/*` is the form that picker is
 * built around.
 *
 * The wildcard alone would not do: iOS and in-app WebViews (Facebook,
 * Instagram) routinely hand back a picked image with an EMPTY `file.type`, and
 * attr-accept matches those on filename only. So the extensions stay, and the
 * two together cover both — verified against real picked-file shapes in
 * upload-accept.test.ts.
 *
 * The short list is also strictly broader than the long one it replaced: the
 * wildcard admits any `image/*` type, including `image/tiff`, which the
 * enumeration had commented out and therefore rejected.
 */
export const PHOTO_ACCEPT_TYPES: Accept = {
  "image/*": [
    ".jpeg",
    ".jpg",
    ".jfif",
    ".png",
    ".gif",
    ".webp",
    ".bmp",
    ".svg",
    ".ico",
    ".apng",
    ".heic",
    ".heif",
    ".avif",
    ".jxl",
    ".jp2",
    ".tif",
    ".tiff",
  ],
};

/**
 * The `accept` attribute string react-dropzone will put on the input, built the
 * same way it builds it. Exported so a test can assert on the thing the browser
 * actually sees rather than on the object we hand to the library.
 */
export function photoAcceptAttribute(): string {
  return Object.entries(PHOTO_ACCEPT_TYPES)
    .reduce<string[]>((acc, [mime, exts]) => [...acc, mime, ...exts], [])
    .join(",");
}
